import {
  MaterialRequestSourceType,
  MaterialRequestStatus,
} from "../constants/material-request.constants";
import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import type { QuotationItemFormRow } from "features/Quotations/utils/quotationItem.utils";
import type {
  CreateMaterialRequestLineItem,
  MaterialRequest,
  MaterialRequestItem,
} from "../types/material-request.types";

/** BOQ row shape for editing a draft MR (preserves optional job line links). */
export type MaterialRequestEditBoqRow = QuotationItemFormRow & {
  workOrderItemId?: number | null;
  pettyWorkItemId?: number | null;
};

/** Parses API quantities whether sent as numbers or decimal strings. */
export function coerceMaterialQty(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Local calendar date as `YYYY-MM-DD` for HTML date inputs. */
export function todayForDateInput(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function materialRequestLineRequestedQty(
  line: MaterialRequestItem,
): number {
  return coerceMaterialQty(line.quantityRequested ?? line.requestedQty);
}

export function materialRequestLineIssuedQty(
  line: MaterialRequestItem,
): number {
  return coerceMaterialQty(line.quantityIssued ?? line.fulfilledQty);
}

/** Gross returns (stock returned against this MR line). */
export function materialRequestLineReturnedQty(
  line: MaterialRequestItem,
): number {
  const legacy = (line as unknown as Record<string, unknown>).quantity_returned;
  return coerceMaterialQty(line.quantityReturned ?? legacy);
}

/** Net fulfilled = issued − returned (floored at 0). */
export function materialRequestLineNetIssuedQty(
  line: MaterialRequestItem,
): number {
  return Math.max(
    0,
    materialRequestLineIssuedQty(line) - materialRequestLineReturnedQty(line),
  );
}

export function materialRequestLineRemaining(
  line: MaterialRequestItem,
): number {
  return Math.max(
    0,
    materialRequestLineRequestedQty(line) -
      materialRequestLineNetIssuedQty(line),
  );
}

/** True if any issue exists or any line has gross issued qty &gt; 0. */
export function materialRequestHasIssuance(record: MaterialRequest): boolean {
  if ((record.materialIssues?.length ?? 0) > 0) return true;
  for (const line of record.items ?? []) {
    if (materialRequestLineIssuedQty(line) > 0) return true;
  }
  return false;
}

/** Approved / partially issued WO / PW / AMC requests — can open issue-stock flow. */
export function materialRequestEligibleForIssueStock(
  record: Pick<MaterialRequest, "status" | "sourceType">,
): boolean {
  const st = record.status;
  return (
    st === MaterialRequestStatus.APPROVED &&
    (record.sourceType === MaterialRequestSourceType.WORK_ORDER ||
      record.sourceType === MaterialRequestSourceType.PETTY_WORK ||
      record.sourceType === "AMC")
  );
}

export function materialRequestEligibleForDelete(
  record: Pick<MaterialRequest, "status">,
): boolean {
  return record.status === MaterialRequestStatus.REQUESTED;
}

export function materialRequestEligibleForEdit(
  record: Pick<MaterialRequest, "status">,
): boolean {
  return record.status === MaterialRequestStatus.REQUESTED;
}

export function materialRequestEligibleForCancel(
  record: MaterialRequest,
): boolean {
  const st = record.status;
  return (
    st === MaterialRequestStatus.REQUESTED ||
    (st === MaterialRequestStatus.APPROVED &&
      !materialRequestHasIssuance(record))
  );
}

/** Approve / reject apply while the request awaits a decision (legacy `PENDING` included). */
export function materialRequestEligibleForApproveReject(
  record: Pick<MaterialRequest, "status">,
): boolean {
  const st = record.status;
  return st === MaterialRequestStatus.REQUESTED;
}

export function materialRequestDisplayCode(record: {
  requestNumber?: string | null;
  code?: string | null;
  id?: number;
}): string {
  const n = record.requestNumber?.trim() || record.code?.trim();
  if (n) return n;
  if (record.id != null) return `#${record.id}`;
  return "—";
}

/** Label for list/overview: prefer nested name, else numeric id, else empty. */
export function materialRequestStockLocationLabel(
  location: { id?: number; name?: string | null } | null | undefined,
  locationId: number | null | undefined,
): string {
  const name = location?.name?.trim();
  if (name) return name;
  const id =
    location?.id != null && Number.isFinite(Number(location.id))
      ? Number(location.id)
      : locationId != null && Number.isFinite(Number(locationId))
        ? Number(locationId)
        : NaN;
  if (Number.isFinite(id) && id > 0) return `Location #${id}`;
  return "";
}

/** Map persisted MR lines into BOQ rows for the draft edit form. */
export function materialRequestItemsToEditBoqRows(
  items: MaterialRequestItem[] | null | undefined,
): MaterialRequestEditBoqRow[] {
  if (!items?.length) return [];
  const out: MaterialRequestEditBoqRow[] = [];
  for (const it of items) {
    const pid = Number(it.productId ?? it.porductId ?? it.product?.id);
    // if (!Number.isFinite(pid) || pid <= 0) continue;
    const qty = materialRequestLineRequestedQty(it);
    if (qty <= 0) continue;
    const rateRaw =
      it.rate ?? it.product?.standardRate ?? it.product?.purchaseCost ?? 0;
    const rate = Number.isFinite(Number(rateRaw)) ? Number(rateRaw) : 0;
    const description = String(it.description ?? "") || "Material";
    const unit =
      String(
        it.unit ?? it.product?.uom?.code ?? it.product?.uom?.name ?? "",
      ).trim() || "PCS";
    const lineId =
      it.id != null && Number.isFinite(Number(it.id)) && Number(it.id) > 0
        ? Number(it.id)
        : undefined;
    out.push({
      ...(lineId != null ? { id: lineId } : {}),
      type: QuotationItemType.MATERIAL,
      description,
      productId: pid,
      quantity: qty,
      unit,
      rate,
      workOrderItemId: it.workOrderItemId ?? undefined,
      pettyWorkItemId: it.pettyWorkItemId ?? undefined,
    });
  }
  return out;
}

/** BOQ rows from the edit form → API `items` on `PUT /material-requests/:id`. */
export function materialRequestEditBoqRowsToUpdateItems(
  rows: MaterialRequestEditBoqRow[],
  sourceType: MaterialRequest["sourceType"],
): CreateMaterialRequestLineItem[] {
  const isWo = sourceType === MaterialRequestSourceType.WORK_ORDER;
  const isPetty = sourceType === MaterialRequestSourceType.PETTY_WORK;
  return rows
    .filter((r) => r.type === QuotationItemType.MATERIAL)
    .map((row) => {
      const base: CreateMaterialRequestLineItem = {
        quantityRequested: Number(row.quantity),
        unit: String(row.unit ?? "").trim() || undefined,
        rate: Number.isFinite(Number(row.rate)) ? Number(row.rate) : null,
        remarks: String(row.description ?? "").trim() || undefined,
        description: String(row.description ?? "").trim() || undefined,
      };
      if (
        isWo &&
        row.workOrderItemId != null &&
        Number(row.workOrderItemId) > 0
      ) {
        base.workOrderItemId = Number(row.workOrderItemId);
      }
      if (
        isPetty &&
        row.pettyWorkItemId != null &&
        Number(row.pettyWorkItemId) > 0
      ) {
        base.pettyWorkItemId = Number(row.pettyWorkItemId);
      }
      return base;
    });
}
